import {
  DEFAULT_PORTAL_LOCALE,
  formatDate,
  formatMoney,
  formatMonth,
  PORTAL_LOCALES,
  tp,
  type PortalLocale,
  type PortalMessageKey,
} from '@wally-reseller/shared';
import { UiLocaleProvider } from '@wally-reseller/ui/locale';
import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';

const STORAGE_KEY = 'portalLocale';

/** Before sign-in: the last choice on this device, else the browser's language, else French. */
function initialLocale(): PortalLocale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && (PORTAL_LOCALES as readonly string[]).includes(stored)) {
      return stored as PortalLocale;
    }
  } catch {
    /* storage unavailable */
  }
  return navigator.language.toLowerCase().startsWith('nl') ? 'nl' : DEFAULT_PORTAL_LOCALE;
}

interface LocaleContextValue {
  locale: PortalLocale;
  setLocale: (locale: PortalLocale) => void;
}

const LocaleContext = createContext<LocaleContextValue>({
  locale: DEFAULT_PORTAL_LOCALE,
  setLocale: () => {},
});

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setState] = useState<PortalLocale>(initialLocale);
  const setLocale = useCallback((next: PortalLocale) => {
    setState(next);
    document.documentElement.lang = next;
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* storage unavailable */
    }
  }, []);
  return (
    <LocaleContext.Provider value={{ locale, setLocale }}>
      <UiLocaleProvider locale={locale}>{children}</UiLocaleProvider>
    </LocaleContext.Provider>
  );
}

export function useLocale(): LocaleContextValue {
  return useContext(LocaleContext);
}

/** `t('portal.nav.leads')` in the current language. */
export function useT() {
  const { locale } = useLocale();
  return useCallback(
    (key: PortalMessageKey, vars?: Record<string, string>) => tp(locale, key, vars),
    [locale],
  );
}

export const LOCALE_NAMES: Record<PortalLocale, string> = { fr: 'Français', nl: 'Nederlands' };

/** "26 octobre 2026" for an ISO timestamp or a `YYYY-MM-DD` day, in the current language. */
export function useFormatDate() {
  const { locale } = useLocale();
  return useCallback(
    (value: string) =>
      formatDate(
        locale,
        new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T12:00:00Z` : value),
      ),
    [locale],
  );
}

/** "3 750,00 €" from integer cents, in the current language. */
export function useFormatMoney() {
  const { locale } = useLocale();
  return useCallback((cents: number) => formatMoney(locale, cents), [locale]);
}

/** "novembre 2026" for `YYYY-MM`, in the current language. */
export function useFormatMonth() {
  const { locale } = useLocale();
  return useCallback((month: string) => formatMonth(locale, month), [locale]);
}
